import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Reveal } from "./Reveal";

const BONSALL = [53.1206, -1.5854];
const TEN_MILES_M = 16093;

const TOWNS = [
  { name: "Bonsall", lat: 53.1206, lng: -1.5854, home: true, dir: "bottom", off: [0, 12] },
  { name: "Matlock", lat: 53.1384, lng: -1.5556, dir: "right", off: [12, 0] },
  { name: "Matlock Bath", lat: 53.1206, lng: -1.5587, dir: "right", off: [12, -14] },
  { name: "Cromford", lat: 53.1125, lng: -1.5328, dir: "right", off: [12, 0] },
  { name: "Tansley", lat: 53.1337, lng: -1.5833, dir: "left", off: [-12, 0] },
  { name: "Darley Dale", lat: 53.1652, lng: -1.5985, dir: "top", off: [0, -10] },
  { name: "Wirksworth", lat: 53.0803, lng: -1.5716, dir: "bottom", off: [0, 12] },
  { name: "Bakewell", lat: 53.2126, lng: -1.6759, dir: "top", off: [0, -10] },
  { name: "Belper", lat: 53.0244, lng: -1.4819, dir: "top", off: [0, -10] },
  { name: "Chesterfield", lat: 53.235, lng: -1.4216, dir: "top", off: [0, -10] },
];

export const CoverageMap = () => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      scrollWheelZoom: false,
      zoomControl: false,
      attributionControl: false,
    }).setView(BONSALL, 10);
    L.control.zoom({ position: "bottomright" }).addTo(map);
    L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", { maxZoom: 18 }).addTo(map);
    L.circle(BONSALL, {
      radius: TEN_MILES_M,
      color: "#FF5722",
      weight: 1.5,
      dashArray: "6 6",
      fillColor: "#FF5722",
      fillOpacity: 0.05,
    }).addTo(map);
    TOWNS.forEach((t) => {
      L.circleMarker([t.lat, t.lng], {
        radius: t.home ? 7 : 4,
        color: "#FF5722",
        weight: t.home ? 3 : 1.5,
        fillColor: t.home ? "#FF5722" : "#0A0A0A",
        fillOpacity: 1,
      })
        .addTo(map)
        .bindTooltip(t.name, {
          permanent: true,
          direction: t.dir || "top",
          offset: t.off || [0, -10],
          className: "map-town-label",
        });
    });
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  return (
    <section id="coverage" data-testid="coverage-section" className="border-t border-line px-5 py-24 md:px-12 md:py-32">
      <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-4">
          <Reveal>
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.25em] text-ember">[ Coverage ]</p>
            <h2 className="font-display text-5xl uppercase leading-[0.85] tracking-tight text-white md:text-6xl">
              10 miles<br />around<br /><span className="text-ember">Bonsall</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-steel">
              Everything inside the ring gets a free site visit — Bonsall,
              Matlock, Cromford, Wirksworth, Bakewell, Belper and the
              villages in between. Just outside it? Ask anyway.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {TOWNS.map((t) => (
                <span
                  key={t.name}
                  data-testid={`coverage-town-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                    t.home
                      ? "border-ember bg-ember text-coal-900 font-bold"
                      : "border-line text-steel hover:border-ember/60 hover:text-white"
                  }`}
                >
                  {t.name}
                </span>
              ))}
            </div>
            <p className="mt-8 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-steel">
              <span className="inline-block h-3 w-3 rounded-full border-2 border-ember bg-ember" aria-hidden="true" />
              Home base — Bonsall, Derbyshire
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.2} className="lg:col-span-8">
          <div className="relative border border-line bg-coal-800 p-2">
            <div
              ref={containerRef}
              data-testid="coverage-map"
              className="h-[380px] w-full md:h-[540px]"
            />
            <p className="absolute left-5 top-5 z-[500] border border-line bg-coal-900/90 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-steel">
              Ring = 10 mi radius · Derbyshire
            </p>
          </div>
          <p className="mt-3 text-right font-mono text-[9px] uppercase tracking-[0.15em] text-steel/50">
            Map data © OpenStreetMap contributors · © CARTO
          </p>
        </Reveal>
      </div>
    </section>
  );
};
